// Test mode utilities — only active when ?test=... is in the URL.
// Used by Playwright to mock auth and subscription state without hitting Firebase.
import type { User } from 'firebase/auth';
import type { SubscriptionInfo } from './hooks/useSubscription';

export interface TestState {
  signedIn: boolean;
  isAdmin?: boolean;
  sub?: Partial<SubscriptionInfo>;
}

const params = new URLSearchParams(window.location.search);
const testParam = params.get('test');

export const isTestMode = !!testParam;

const TEST_STATES: Record<string, TestState> = {
  'visitor': { signedIn: false },
  'visitor-mobile': { signedIn: false },
  'signed-in-none': { signedIn: true, sub: { status: 'none' } },
  'signed-in-none-after-trial': {
    signedIn: true,
    sub: { status: 'none', hasUsedTrial: true, hasStripeCustomer: true },
  },
  'signed-in-trial': {
    signedIn: true,
    sub: {
      status: 'active', plan: 'monthly', paidVia: 'stripe',
      inTrial: true, trialDaysLeft: 23, hasStripeCustomer: true, hasUsedTrial: true,
      currentPeriodEnd: new Date(Date.now() + 23 * 86400000),
    },
  },
  'signed-in-trial-cancelled': {
    signedIn: true,
    sub: {
      status: 'active', plan: 'monthly', paidVia: 'stripe',
      inTrial: true, trialDaysLeft: 23, hasStripeCustomer: true, hasUsedTrial: true,
      cancelAtPeriodEnd: true,
      currentPeriodEnd: new Date(Date.now() + 23 * 86400000),
    },
  },
  'signed-in-monthly': {
    signedIn: true,
    sub: {
      status: 'active', plan: 'monthly', paidVia: 'stripe',
      inTrial: false, hasStripeCustomer: true, hasUsedTrial: true,
      currentPeriodEnd: new Date(Date.now() + 14 * 86400000),
    },
  },
  'signed-in-yearly': {
    signedIn: true,
    sub: {
      status: 'active', plan: 'yearly', paidVia: 'stripe',
      inTrial: false, hasStripeCustomer: true, hasUsedTrial: true,
      currentPeriodEnd: new Date(Date.now() + 200 * 86400000),
    },
  },
  'signed-in-code': {
    signedIn: true,
    sub: { status: 'active', plan: 'code', paidVia: 'code', codeDaysLeft: 287 },
  },
  // User just completed Stripe checkout — webhook hasn't fired yet so no active sub.
  // Used to test the CheckoutSuccess waiting flow.
  'signed-in-pending': {
    signedIn: true,
    sub: { status: 'none' },
  },
  'signed-in-expired': {
    signedIn: true,
    sub: { status: 'expired', hasStripeCustomer: true, hasUsedTrial: true },
  },
  // Trial expired but webhook hasn't fired yet — trialEnd in the past, paid still true.
  // Should NOT show "0 days of free credit remaining" — should fall through to active billing view.
  'signed-in-trial-just-ended': {
    signedIn: true,
    sub: {
      status: 'active', plan: 'monthly', paidVia: 'stripe',
      inTrial: false, // trial ended, no longer in trial
      trialEnd: new Date(Date.now() - 1 * 86400000), // yesterday
      trialDaysLeft: null,
      hasStripeCustomer: true, hasUsedTrial: true,
      currentPeriodEnd: new Date(Date.now() + 29 * 86400000),
    },
  },
  // Cancelled-during-trial state where the trial has now ended and no current period exists.
  // The hook should resolve this to 'expired' so user sees the plan picker, not "Reactivate".
  'signed-in-cancelled-trial-ended': {
    signedIn: true,
    sub: {
      status: 'expired', // ← derived: paid was true but cancelAtPeriodEnd + access ended
      plan: 'yearly', paidVia: 'stripe',
      inTrial: false,
      trialEnd: new Date(Date.now() - 1 * 86400000),
      trialDaysLeft: null,
      cancelAtPeriodEnd: true,
      currentPeriodEnd: null,
      hasStripeCustomer: true, hasUsedTrial: true,
    },
  },
};

// In test mode we let the test temporarily override the signed-in flag via sessionStorage,
// so a "visitor" can become "signed in" mid-flow without losing the test param.
const sessionOverride = isTestMode ? sessionStorage.getItem('gnosis-test-signed-in') : null;

const baseState = isTestMode ? (TEST_STATES[testParam!] || null) : null;
export const testState: TestState | null = baseState && sessionOverride === '1'
  ? { ...baseState, signedIn: true, sub: baseState.sub || { status: 'none' } }
  : baseState;

export function markTestSignedIn() {
  if (isTestMode) sessionStorage.setItem('gnosis-test-signed-in', '1');
}

// A mock user constructed from test state. Even visitors get one returned here so
// signInWithGoogle() in test mode can switch them to "signed in" state instantly.
export const buildMockUser = (admin = false): User => ({
  uid: admin ? 'bZj8y9DApEac3eKOuKV08MKcaDx2' : 'test-uid',
  email: 'test@example.com',
  displayName: 'Test User',
  metadata: { creationTime: new Date().toISOString() },
} as unknown as User);

export const mockUser: User | null = testState?.signedIn
  ? buildMockUser(testState.isAdmin)
  : null;

// Re-export for the auth hook to call when sign-in is simulated
export const mockSignIn = () => {
  markTestSignedIn();
};

const emptySub: SubscriptionInfo = {
  status: null,
  loading: false,
  plan: null,
  paidVia: null,
  inTrial: false,
  trialEnd: null,
  trialDaysLeft: null,
  currentPeriodEnd: null,
  codeExpiresAt: null,
  codeDaysLeft: null,
  hasStripeCustomer: false,
  cancelAtPeriodEnd: false,
  hasUsedTrial: false,
  janitor: false,
  promotion: null,
  promoHeldDays: null,
};

export const mockSub: SubscriptionInfo | null = testState
  ? { ...emptySub, ...(testState.sub || {}) }
  : null;

// Allow tests to simulate async sub state changes (e.g. webhook firing).
// Tests call window.__gnosisUpdateSub(patch) to trigger a re-render with new sub state.
type SubListener = (patch: Partial<SubscriptionInfo>) => void;
const subListeners: SubListener[] = [];
export function onTestSubUpdate(fn: SubListener) { subListeners.push(fn); return () => { const i = subListeners.indexOf(fn); if (i >= 0) subListeners.splice(i, 1); }; }
if (isTestMode && typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__gnosisUpdateSub = (patch: Partial<SubscriptionInfo>) => {
    subListeners.forEach((fn) => fn(patch));
  };
}
